import {
  type CustomerOrder,
  type ProductionType,
  type Scope,
  type TitleProject,
  type TurnaroundDays,
  titleDraftOptionsSchema,
} from '@noconflict/contracts';
import { useCallback, useRef, useState } from 'react';
import { orderErrorMessage, useUpdateOrderDraft } from '../../../entities/order';
import { useUpdateTitleProject } from '../../../entities/title-project';

/** What the form holds: text exactly as typed, choices as chosen (a scope is null until one is). */
export type TitleDraftValues = {
  title: string;
  productionType: ProductionType | null;
  company: string;
  notes: string;
  scope: Scope | null;
  turnaroundDays: TurnaroundDays;
  reference: string;
};

export type MissingField = 'title' | 'productionType' | 'scope' | 'reference';

export type TitleDraft = {
  values: TitleDraftValues;
  /** Changes a value on screen only; `save` (on blur) persists it. */
  set: (patch: Partial<TitleDraftValues>) => void;
  /** Changes a value and saves at once: selects and radio cards. */
  commit: (patch: Partial<TitleDraftValues>) => void;
  /** Saves whatever differs from what was last saved. */
  save: () => void;
  /** Resolves once every pending save has finished; false when the last one failed. */
  flush: () => Promise<boolean>;
  status: 'idle' | 'saving' | 'saved' | 'error';
  error: string | null;
  /** What a submit still needs. */
  missing: MissingField[];
};

const detailsOf = (v: TitleDraftValues) =>
  JSON.stringify([v.title.trim(), v.productionType, v.company.trim(), v.notes.trim()]);
const orderOf = (v: TitleDraftValues) =>
  JSON.stringify([v.scope, v.turnaroundDays, v.reference.trim()]);

/**
 * The state of the title order form. Every field saves by itself (text when the field is left, a choice when it
 * is made): details through `update_title_project`, the priced options and the reference through the order
 * draft. Saves are queued, so they reach the database in the order they were made.
 */
export function useTitleDraft({
  projectId,
  details,
  order,
  referenceRequired,
}: {
  projectId: string;
  details: TitleProject;
  order: CustomerOrder;
  referenceRequired: boolean;
}): TitleDraft {
  const saveDetails = useUpdateTitleProject(projectId);
  const saveOrder = useUpdateOrderDraft();
  const [values, setValues] = useState<TitleDraftValues>(() => {
    const options = titleDraftOptionsSchema.safeParse(order.options).data;
    return {
      title: details.title ?? '',
      productionType: details.production_type,
      company: details.production_company ?? '',
      notes: details.notes ?? '',
      scope: options?.scope ?? null,
      turnaroundDays: options?.turnaroundDays ?? 5,
      reference: order.reference ?? '',
    };
  });
  const [status, setStatus] = useState<TitleDraft['status']>('idle');
  const [error, setError] = useState<string | null>(null);
  // The latest values and what the database holds, outside React's render cycle: a save reads them when it runs.
  const latest = useRef(values);
  const saved = useRef({ details: detailsOf(values), order: orderOf(values) });
  const queue = useRef<Promise<boolean>>(Promise.resolve(true));
  const { mutateAsync: mutateDetails } = saveDetails;
  const { mutateAsync: mutateOrder } = saveOrder;
  const orderId = order.id;

  const persist = useCallback(async (): Promise<boolean> => {
    const v = latest.current;
    const next = { details: detailsOf(v), order: orderOf(v) };
    if (next.details === saved.current.details && next.order === saved.current.order) return true;
    setStatus('saving');
    setError(null);
    try {
      if (next.details !== saved.current.details) {
        await mutateDetails({
          title: v.title.trim() || null,
          productionType: v.productionType,
          productionCompany: v.company.trim() || null,
          notes: v.notes.trim() || null,
        });
        saved.current.details = next.details;
      }
      if (next.order !== saved.current.order) {
        await mutateOrder({
          orderId,
          draft: {
            product: 'title',
            options: { scope: v.scope, turnaroundDays: v.turnaroundDays },
            reference: v.reference.trim() || null,
          },
        });
        saved.current.order = next.order;
      }
      setStatus('saved');
      return true;
    } catch (e) {
      setStatus('error');
      setError(orderErrorMessage(e));
      return false;
    }
  }, [mutateDetails, mutateOrder, orderId]);

  const save = useCallback(() => {
    queue.current = queue.current.then(persist, persist);
  }, [persist]);

  const set = useCallback((patch: Partial<TitleDraftValues>) => {
    latest.current = { ...latest.current, ...patch };
    setValues(latest.current);
  }, []);

  const commit = useCallback(
    (patch: Partial<TitleDraftValues>) => {
      set(patch);
      save();
    },
    [set, save],
  );

  const flush = useCallback(() => {
    save();
    return queue.current;
  }, [save]);

  const missing: MissingField[] = [];
  if (values.title.trim() === '') missing.push('title');
  if (values.productionType === null) missing.push('productionType');
  if (values.scope === null) missing.push('scope');
  if (referenceRequired && values.reference.trim() === '') missing.push('reference');

  return { values, set, commit, save, flush, status, error, missing };
}
